// ng-packagr only compiles files reachable from the public API, so the ambient declaration for
// exif-parser has to be referenced explicitly; an import cannot target a non-module .d.ts.
// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="./exif-parser.d.ts" />
import * as exifParser from 'exif-parser';

interface CanvasOptions {
    translate: {
        x: number;
        y: number;
    };
    scale: {
        x: number;
        y: number;
    };
    rotate: {
        angle: number;
    };
}

export class ImageRotater {

    public static async rotateImage(base64Data: string): Promise<string> {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        // eslint-disable-next-line
        if (!context) {
            throw new Error('canvas can not created');
        }
        const image = await this.readImage(base64Data);

        const orientation = this.getOrientation(base64Data);
        const {width, height} = this.getSize(
            orientation > 4 ? image.height : image.width,
            orientation > 4 ? image.width : image.height,
            1024,
        );
        canvas.setAttribute('width', `${width}px`);
        canvas.setAttribute('height', `${height}px`);
        const {translate, scale, rotate} = this.getCanvasOptions(
            width,
            height,
            orientation,
        );
        context.translate(translate.x, translate.y);
        context.scale(scale.x, scale.y);
        context.rotate(rotate.angle);

        // exif orientation values > 4 correspond to portrait orientation.
        // width and height parameters must be swapped for landscape to ensure correct image display
        if (orientation > 4) {
            context.drawImage(image, 0, 0, height, width);
        } else {
            context.drawImage(image, 0, 0, width, height);
        }

        return canvas.toDataURL('image/jpeg');
    }

    private static async readImage(src: string): Promise<HTMLImageElement> {
        const img = new Image();
        return new Promise((resolve, reject) => {
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            img.onload = () => resolve(img);
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/prefer-promise-reject-errors
            img.onerror = error => reject(error);
            img.src = src;
        });
    }


    private static getSize(width: number, height: number, maxSize: number): { width: number, height: number } {
        if (width > height) {
            return {
                width: maxSize,
                height: height * (maxSize / width),
            };
        }
        if (height > width) {
            return {
                width: width * (maxSize / height),
                height: maxSize,
            };
        }
        return {
            width: maxSize,
            height: maxSize,
        };
    }

    private static getOrientation(imageAsBase64: string): number {
        const data = this.uriToArrayBuffer(imageAsBase64);

        try {

            const tag = exifParser.create(data).parse();

            // A missing orientation tag is handled like the default orientation 1 (no transformation).
            return tag.tags['Orientation'] ?? 1;
        } catch (e) {
            return 1;
        }
    }

    private static uriToArrayBuffer(dataURI: string): ArrayBuffer {
        // convert base64 to raw binary data held in a string
        // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
        const byteString = atob(dataURI.split(',')[1]);

        // write the bytes of the string to an ArrayBuffer
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        // write the ArrayBuffer to a blob, and you're done
        return ab;
    }

    private static getCanvasOptions(width: number, height: number, orientation: number): CanvasOptions {
        const options: CanvasOptions = {
            translate: {
                x: 0,
                y: 0,
            },
            scale: {
                x: 1,
                y: 1,
            },
            rotate: {
                angle: 0,
            },
        };

        switch (orientation) {
            case 2:
                // horizontal flip
                return {
                    ...options,
                    translate: {
                        ...options.translate,
                        x: width,
                    },
                    scale: {
                        ...options.scale,
                        x: -1,
                    },
                };
            case 3:
                // 180° rotate left
                return {
                    ...options,
                    translate: {
                        x: width,
                        y: height,
                    },
                    rotate: {
                        angle: Math.PI,
                    },
                };
            case 4:
                // vertical flip
                return {
                    ...options,
                    translate: {
                        ...options.translate,
                        y: height,
                    },
                    scale: {
                        ...options.scale,
                        y: -1,
                    },
                };
            case 5:
                // vertical flip + 90 rotate right
                return {
                    ...options,
                    scale: {
                        ...options.scale,
                        x: -1,
                    },
                    rotate: {
                        angle: (90 * Math.PI) / 180,
                    },
                };
            case 6:
                // 90° rotate right
                return {
                    ...options,
                    translate: {
                        ...options.translate,
                        x: width,
                    },
                    rotate: {
                        angle: (90 * Math.PI) / 180,
                    },
                };
            case 7:
                // horizontal flip + 90 rotate right
                return {
                    ...options,
                    translate: {
                        x: width,
                        y: height,
                    },
                    rotate: {
                        angle: (90 * Math.PI) / 180,
                    },
                    scale: {
                        ...options.scale,
                        y: -1,
                    },
                };
            case 8:
                // 90° rotate left
                return {
                    ...options,
                    translate: {
                        ...options.translate,
                        y: height,
                    },
                    rotate: {
                        angle: -(90 * Math.PI) / 180,
                    },
                };
            default:
                return options;
        }
    }

}
